import React, { useState, useRef, useEffect } from 'react';
import { dt } from 'src/utils/documentsI18n';
import { createPortal } from 'react-dom';
import './PaymentMethodDropdown.scss';

const DocumentActionsDropdown = ({
  onDelete,
  onCopy,
  onDownloadMatches,
  onUndoMatches,
  showCopy = false,
  disabled = false,
  variant = 'light',
  direction = 'down',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const isDark = variant === 'dark';

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const updatePosition = () => {
      if (!buttonRef.current) return;
      const buttonRect = buttonRef.current.getBoundingClientRect();
      if (direction === 'up') {
        setMenuPosition({
          top: buttonRect.top - 4,
          left: buttonRect.left,
          transform: 'translateY(-100%)',
        });
      } else {
        setMenuPosition({
          top: buttonRect.bottom + 4,
          left: buttonRect.left,
        });
      }
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, direction]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (dropdownRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const closeAnd = (fn) => {
    setIsOpen(false);
    fn?.();
  };

  const menuItems = [
    ...(onDownloadMatches ? [{ id: 'download-matchs', label: dt('actionsMenu.downloadMatches'), onClick: () => closeAnd(onDownloadMatches) }] : []),
    ...(onDelete ? [{ id: 'delete', label: dt('actionsMenu.delete'), onClick: () => closeAnd(onDelete) }] : []),
    ...(showCopy ? [{ id: 'copy', label: dt('actionsMenu.copy'), onClick: () => closeAnd(onCopy) }] : []),
    ...(onUndoMatches ? [{ id: 'undo-matchs', label: dt('actionsMenu.deleteMatches'), dividerBefore: true, onClick: () => closeAnd(onUndoMatches) }] : []),
  ];

  return (
    <div
      ref={dropdownRef}
      className={`payment-method-dropdown ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${isDark ? 'variant-dark' : ''}`}
    >
      <div className="payment-method-dropdown-wrapper">
        <button
          ref={buttonRef}
          type="button"
          className={`payment-method-dropdown-trigger ${isDark ? 'variant-dark' : ''}`}
          onClick={() => !disabled && setIsOpen((v) => !v)}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span className="payment-method-dropdown-text">{dt('actionsMenu.actions')}</span>
          <span className="payment-method-dropdown-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
              <path stroke={isDark ? "#e5e7eb" : "#6b7280"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
            </svg>
          </span>
        </button>
      </div>
      {isOpen && !disabled && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className={`payment-method-dropdown-menu payment-method-dropdown-menu-portal ${isDark ? 'variant-dark' : ''} ${direction === 'up' ? 'direction-up' : ''}`}
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            ...(direction === 'up' && menuPosition.transform ? { transform: menuPosition.transform } : {}),
          }}
        >
          <ul className={`payment-method-dropdown-options ${isDark ? 'variant-dark' : ''}`} role="listbox">
            {menuItems.map((item) => (
              <React.Fragment key={item.id}>
                {item.dividerBefore && (
                  <li
                    className="payment-method-option-divider"
                    role="separator"
                    aria-hidden="true"
                    style={{
                      height: '1px',
                      width: '90%',
                      padding: 0,
                      margin: '4px auto',
                      background: isDark ? '#374151' : '#e5e7eb',
                      listStyle: 'none',
                    }}
                  />
                )}
                <li
                  className={`payment-method-option ${isDark ? 'variant-dark' : ''}`}
                  onClick={item.onClick}
                  role="option"
                  tabIndex={0}
                >
                  <span className="payment-method-option-text">{item.label}</span>
                </li>
              </React.Fragment>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

export default DocumentActionsDropdown;
